import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface PageTopBarConfig {
  breadcrumb?: string[];
  actions?: React.ReactNode;
  /** Bump when `actions` content changes so AppShell re-reads the ref (e.g. demo state). */
  actionsVersion?: unknown;
  /**
   * Visual redesign direction D: a page that renders its own tall PageBand
   * suppresses the shared compact top bar so the band isn't doubled.
   */
  hideTopBar?: boolean;
}

interface PageTopBarContextValue {
  breadcrumb?: string[];
  actionsRef: React.RefObject<React.ReactNode>;
  actionsTick: number;
  hideTopBar: boolean;
  setBreadcrumb: (breadcrumb: string[] | undefined) => void;
  setHideTopBar: (hide: boolean) => void;
  syncActions: () => void;
}

const PageTopBarContext = createContext<PageTopBarContextValue | null>(null);

function breadcrumbKey(breadcrumb: string[] | undefined): string {
  return breadcrumb?.length ? JSON.stringify(breadcrumb) : '';
}

export function PageTopBarProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumb, setBreadcrumbState] = useState<string[] | undefined>();
  const [actionsTick, setActionsTick] = useState(0);
  const [hideTopBar, setHideTopBar] = useState(false);
  const actionsRef = useRef<React.ReactNode>(null);

  const setBreadcrumb = useCallback((next: string[] | undefined) => {
    setBreadcrumbState((prev) => {
      const prevKey = breadcrumbKey(prev);
      const nextKey = breadcrumbKey(next);
      return prevKey === nextKey ? prev : next;
    });
  }, []);

  const syncActions = useCallback(() => {
    setActionsTick((t) => t + 1);
  }, []);

  const value = useMemo((): PageTopBarContextValue => {
    const next: PageTopBarContextValue = {
      actionsRef,
      actionsTick,
      hideTopBar,
      setBreadcrumb,
      setHideTopBar,
      syncActions,
    };
    if (breadcrumb !== undefined) next.breadcrumb = breadcrumb;
    return next;
  }, [breadcrumb, actionsTick, hideTopBar, setBreadcrumb, syncActions]);

  return <PageTopBarContext.Provider value={value}>{children}</PageTopBarContext.Provider>;
}

export function usePageTopBarContext() {
  const ctx = useContext(PageTopBarContext);
  if (!ctx) {
    throw new Error('usePageTopBarContext must be used within PageTopBarProvider');
  }
  return ctx;
}

export function usePageTopBar(config: PageTopBarConfig) {
  const { actionsRef, setBreadcrumb, setHideTopBar, syncActions } = usePageTopBarContext();
  actionsRef.current = config.actions ?? null;

  const key = breadcrumbKey(config.breadcrumb);
  const actionsVersion = config.actionsVersion ?? key;
  const hideTopBar = config.hideTopBar ?? false;

  useLayoutEffect(() => {
    setBreadcrumb(key ? (JSON.parse(key) as string[]) : undefined);
    setHideTopBar(hideTopBar);
    syncActions();
    return () => {
      setBreadcrumb(undefined);
      setHideTopBar(false);
    };
  }, [setBreadcrumb, setHideTopBar, syncActions, key, actionsVersion, hideTopBar]);
}
